'use client';

import { Button } from '@common/components';
import { SvgChevronright } from '@common/icon';
import { cn } from '@core/utils';
import type { HomeDonationBusinessDto } from '@generated/unauth';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { CustomLink } from '@/app/_components/CustomLink';
import { SPECIFIC_DONATION_STATUS } from '@/app/donation/constants';
import { Image } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface HometownsBusinessSectionProps {
  className?: string;
  list: HomeDonationBusinessDto[];
}

export const HometownsBusinessSection = ({
  className,
  list,
}: HometownsBusinessSectionProps) => {
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSwiperReady, setIsSwiperReady] = useState(false);
  const { ref: inViewRef, inView } = useInView({ threshold: 0.3 });

  //* Autoplay 플러그인 설정
  const autoplayRef = useRef(
    Autoplay({ delay: 3000, stopOnInteraction: false }),
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'center',
    },
    [autoplayRef.current],
  );

  //* 뷰포트 진입 시에만 autoplay 활성화
  useEffect(() => {
    if (!emblaApi) return;

    if (inView) {
      autoplayRef.current?.play();
    } else {
      autoplayRef.current?.stop();
    }
  }, [inView, emblaApi]);

  //* 슬라이드 변경 이벤트 리스너
  useEffect(() => {
    if (!emblaApi) return;

    const handleSelect = () => {
      const index = emblaApi.selectedScrollSnap();
      setActiveIndex(index);
    };

    emblaApi.on('select', handleSelect);

    return () => {
      emblaApi.off('select', handleSelect);
    };
  }, [emblaApi]);

  //* embla 인스턴스가 준비되면 슬라이더를 화면에 표시
  useEffect(() => {
    if (!emblaApi) return;
    setIsSwiperReady(true);
  }, [emblaApi]);

  if (list.length === 0 || !isDesktop) return null;

  const previndex = activeIndex === 0 ? list.length - 1 : activeIndex - 1;
  const nextIndex = activeIndex === list.length - 1 ? 0 : activeIndex + 1;

  return (
    <section
      ref={inViewRef}
      className={cn(
        'relative flex w-full flex-col items-center overflow-hidden',
        className,
      )}
    >
      <div
        className={cn('w-full pb-110 pt-50 transition-opacity duration-300', {
          'opacity-0': !isSwiperReady,
          'opacity-100': isSwiperReady,
        })}
        ref={emblaRef}
      >
        <div className="flex">
          {list.map(
            (
              {
                donationBusinessId: donation_business_id,
                institutionLogoUrl: institution_logo_url,
                institutionName: institution_name,
                thumbnailUrl: thumbnail_url,
                achievementRate: achievement_rate,
                donationAmount: donation_amount,
                donationBusinessName: donation_business_name,
                dday,
                specificDonationStatus,
              },
              index,
            ) => {
              const isBlur = index === previndex || index === nextIndex;
              const isActiveIndex = index === activeIndex;
              const isHide =
                !isActiveIndex && index !== previndex && index !== nextIndex;

              return (
                <div
                  className={cn('relative z-0 flex-[0_0_auto]', {
                    'z-10': !isBlur,
                  })}
                  key={index}
                  onClick={() => autoplayRef.current?.stop()}
                >
                  <CustomLink
                    href={DONATION_ROUTES.HOMETOWN_BUSINESS_DETAIL.pathname({
                      id: donation_business_id,
                    })}
                    aria-label={`'${donation_business_name}' 지정기부사업 상세 페이지로 이동`}
                    className={cn(
                      'relative flex w-[75rem] max-w-full flex-col gap-120 overflow-hidden rounded-[40px] border-x border-t border-Gray-black/5 p-64',
                      'duration-400 opacity-100 transition-all ease-in-out',
                      {
                        'shadow-[0_20px_19px_0_rgba(0,0,0,0.06),0_0_8px_0_rgba(0,0,0,0.03)]':
                          isActiveIndex,
                        'blur-sm': isBlur,
                        'scale-[1.2]': !isBlur,
                        'opacity-0': isHide,
                      },
                    )}
                  >
                    {/* 배경 이미지 - 데스크톱에서만 로드 */}
                    <Image
                      src={thumbnail_url}
                      alt={`${donation_business_name} 썸네일 이미지`}
                      fill
                      className="-z-10 object-cover"
                      sizes={`(max-width: ${BREAKPOINTS.MOBILE_TABLET}) 0px, 1198px`}
                    />

                    {/* 그라디언트 오버레이 */}
                    <div
                      className="absolute inset-0 z-0"
                      style={{
                        background:
                          'linear-gradient(270deg, rgba(0,0,0,0.56) 0%, rgba(0,0,0,0.00) 35%, rgba(0,0,0,0.56) 100%)',
                      }}
                    />
                    <p className="relative z-10 line-clamp-2 h-68 max-w-[24.3125rem] font-800 text-Gray-white typo-h4">
                      현재 진행중인 고향 사업을 살펴보고 따뜻한 손길을 내밀어요.
                    </p>

                    <div className="relative z-10 flex w-full flex-col">
                      <div className="flex items-center justify-between pb-12">
                        <div className="flex items-center gap-4">
                          <div className="relative size-24 overflow-hidden rounded-pill border-[1.2px] border-Gray-300">
                            <Image
                              fill
                              src={institution_logo_url}
                              alt={`${institution_name} 로고 이미지`}
                              className="object-cover"
                              sizes="24px"
                            />
                          </div>
                          <p className="font-600 text-Gray-200 typo-body4">
                            {institution_name}
                          </p>
                        </div>

                        <p className="font-600 text-Gray-200 typo-body3">
                          {specificDonationStatus ===
                            SPECIFIC_DONATION_STATUS.ENDED || dday < 0
                            ? '마감'
                            : dday === 0
                              ? '오늘 마감'
                              : `${dday}일 남음`}
                        </p>
                      </div>

                      <p className="pb-32 font-600 text-Gray-white typo-h6">
                        {donation_business_name}
                      </p>

                      <div className="relative mb-16 h-10 w-full rounded-pill bg-Gray-white/60">
                        <div
                          className="h-full rounded-pill bg-B-500"
                          style={{
                            width: `${achievement_rate > 100 ? 100 : achievement_rate}%`,
                          }}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <p className="font-700 text-B-500 typo-h6">
                          {achievement_rate}%
                        </p>
                        <p className="font-700 text-Gray-white typo-h6">
                          {donation_amount.toLocaleString()} 원
                        </p>
                      </div>
                    </div>

                    <div
                      className={cn(
                        'absolute inset-0 size-full bg-Gray-black/60 opacity-0 transition-opacity duration-300',
                        {
                          'opacity-100': isBlur,
                        },
                      )}
                    />
                  </CustomLink>
                </div>
              );
            },
          )}
        </div>
      </div>
      <Button theme="sub-line" dimensions="H56" asChild>
        <CustomLink
          className="flex w-[30rem] max-w-full items-center gap-6"
          href={DONATION_ROUTES.HOMETOWNS_BUSINESS.pathname}
          aria-label="전체 지정기부사업 목록 페이지로 이동"
        >
          지정기부사업 전체보기
          <SvgChevronright className="size-24 text-Gray-600" />
        </CustomLink>
      </Button>
    </section>
  );
};
